import axios from 'axios';
import { isNil } from 'lodash';
import { socketHandler } from '@fiverr-private/futile';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import stats from '../../../../lib/stats';
import {
    DEFAULT_ACTION,
    DEFAULT_CONTROLLER,
    FAILED_TO_INITIALIZE,
    REALTIME_CONNECTION_EVENT
} from '../constants';
import { METRICS } from '../lib';
import { MODULE_NAME } from '../../constants';

/**
 * @typedef {Object} SocketConfiguration
 * @property {string} url - The route we should send our connection request to.
 * @property {string} username - of the current user.
 * @property {string} token - a JWT token of the current user.
 * @property {string} subscription
 */

/**
 * @typedef {Object} ConnectSocketPayload
 * @property {string} event
 * @property {Object} payload
 * @property {string} payload.username
 * @property {string} payload.token
 * @property {string} payload.controller
 * @property {string} payload.subscription
 * @property {string} payload.action
 */

/**
 * The endpoint which should be used
 * to tell the server we wish to establish a
 * connection, and provides us the configurations
 * we need in order to do so.
 */
export const SOCKET_CONFIG_URL = '/realtime/websocket';

/**
 * Returns true when the user is logged out.
 * Otherwise, returns false.
 * @returns {boolean}
 */
const userIsLoggedOut = () => {
    const { userId } = getContext();
    return isNil(userId);
};

const countConnectionSuccess = () => {
    stats.count(METRICS.CONNECT_SUCCESS);
};

const countConnectionFailure = () => {
    stats.count(METRICS.CONNECT_FAILURE);
};

const countConnectionSkipped = () => {
    stats.count(METRICS.CONNECT_SKIPPED);
};

/**
 * Prepares the configuration to match
 * the form expected by the server.
 * @param {SocketConfiguration} config
 * @returns {ConnectSocketPayload}
 */
const makePayload = (config) => ({
    event: REALTIME_CONNECTION_EVENT,
    payload: {
        username: config.username,
        token: config.token,
        controller: DEFAULT_CONTROLLER,
        action: DEFAULT_ACTION,
        subscription: config.subscription
    }
});

const countFetchConfigSuccess = () => {
    stats.count(METRICS.FETCH_CONFIGURATION_SUCCESS);
};

const countFetchConfigFailure = () => {
    stats.count(METRICS.FETCH_CONFIGURATION_FAILURE);
};

/**
 * Enriches and logs an error.
 * @param {Error} error
 */
const logError = (error) => {
    logger.error(error, {
        module: MODULE_NAME,
        message: FAILED_TO_INITIALIZE
    });
};

/**
 * Fetches the configurations required
 * for establishing the socket connection.
 * @returns {SocketConfiguration}
 */
const fetchConfig = async() => {
    try {
        const { data } = await axios.post(SOCKET_CONFIG_URL);
        countFetchConfigSuccess();

        return data;
    } catch (error) {
        countFetchConfigFailure();
        throw error;
    }
};

/**
 * Initializes the global socketHandler used
 * by Fit.
 */
export const init = async() => {
    try {
        if (userIsLoggedOut()) {
            countConnectionSkipped();
            return;
        }

        const config = await fetchConfig();
        socketHandler.connect(config.url, makePayload(config));

        countConnectionSuccess();

    } catch (error) {
        countConnectionFailure();
        logError(error);
    }
};
